/**
 * One control, for the firm (Phase 4B, reordered in 5D by DEC-49). Four tabs. "About this control" reads what the
 * control is, its current rating, where it stands, how to put it in place, and what was reported in 2025.
 * "Control assistant" is where the IT lead talks to the assistant. "CDW findings" is what the assessors found.
 * "Risk register" is the linked risks, plans and risk acceptance. National IT and the Board read it read-only.
 */
import {
  ACCEPTANCE_STATUS,
  areaName,
  CDW_GAP_STATUS,
  CONTROL_SUMMARIES,
  DELIVERY_STATUS,
  FAMILY_LINK_LABEL,
  maturityLabel,
  RISK_IMPACT,
  SELF_STATUS,
  SUBMISSION_STATUS,
  workState,
  resultLabel,
  SURVEY_STATUS,
} from '@grc/shared/vocabulary';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, useApi, useApiClient } from '../api';
import { acceptanceLine, AnswerSection, formatDate, Load, PageHeader, RiskChip, Status, WaveTag } from '../components';
import { ExplainedPanel } from '../Explained';
import { ImplementationGuide } from '../Implementation';
import { useViewParam, ViewTabs } from '../tabs';
import type { FirmControlDetail, RiskAcceptance } from '../types';
import { AssistantSide, DraftEditor, latestDraft, Submissions, type AssistantState, type DraftRef } from './AssistantPanel';
import { NetworkBanner, SeverityChip } from './FirmOverview';

/** Where the current rating comes from; PL-08 (5E pre-gate): the date follows it once, with no doubled year. */
const SOURCE: Record<string, string> = {
  cdw: 'Assessed by CDW, 2026',
  reviewed: 'Reviewed by National IT',
  self: "The firm's own rating",
};

/** A rating the firm saved but never sent (DEC-31), by the shared worklist rule the row that opens this page uses. */
const unsent = (d: FirmControlDetail) =>
  workState({
    reviewState: d.latestSubmission?.status === 'submitted' || d.latestSubmission?.status === 'returned' ? d.latestSubmission.status : null,
    reviewedAt: d.nationalReview?.reviewedAt ?? null,
    currentSource: null,
    selfStatus: d.selfAssessment?.status ?? null,
    selfUpdatedAt: d.selfAssessment?.updatedAt ?? null,
    cdwStatus: d.cdw?.status ?? null,
  }) === 'draft';

const statusText = (k: string) => resultLabel(k);

/**
 * A section with its own look (Andres, 2026-09-25): a tinted header band with a short label, so the
 * assessors' findings, the firm's risks and plans, and its decisions read as different kinds of content.
 */
function Section({ kind, question, id, anchor, children }: { kind: 'assessors' | 'survey' | 'plans' | 'decision'; question: string; id: string; anchor?: string; children: ReactNode }) {
  // 5E NEXT PASS 1 (E5): the question as the heading, in the shared AnswerSection; no small label above it. The kind
  // keeps its coloured edge.
  return (
    <AnswerSection id={id} level={2} anchor={anchor} className={`sec sec-${kind}`} question={question}>
      {children}
    </AnswerSection>
  );
}

/**
 * The page's tabs: what the control is, the control assistant, the CDW findings, and the risk register.
 * Existing sections move under those tabs. The tab is in the address (?tab=); a question handed over (?ask=) or a
 * worklist link (#assistant) opens the assistant; #acceptances opens the risk register.
 */
type Tab = 'about' | 'assistant' | 'findings' | 'register';
const TABS: Array<{ id: Tab; label: string; short?: string }> = [
  { id: 'about', label: 'About this control', short: 'About' },
  { id: 'assistant', label: 'Control assistant', short: 'Assistant' },
  { id: 'findings', label: 'CDW findings', short: 'Findings' },
  { id: 'register', label: 'Risk register', short: 'Risks' },
];

export function FirmControl() {
  const { firmCode, controlId } = useParams();
  const state = useApi<FirmControlDetail>(`/firms/${firmCode}/controls/${controlId}`);
  const asst = useApi<AssistantState>(`/firms/${firmCode}/controls/${controlId}/assistant`);
  const [draft, setDraft] = useState<DraftRef | null | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const call = useApiClient();
  const shownDraft = draft === undefined ? latestDraft(asst.data) : draft;
  const [params] = useSearchParams();
  const { hash } = useLocation();
  // An address without ?tab= opens the assistant when it hands a question over (?ask=) or comes from a worklist link,
  // the risk register for a link to the acceptance, and the findings for a link to what the assessors found.
  const fallback: Tab =
    params.get('ask') || hash === '#assistant' || hash === '#assistant-h'
      ? 'assistant'
      : hash === '#acceptances' || hash === '#acceptance-h'
        ? 'register'
        : hash === '#assessors-h'
          ? 'findings'
          : 'about';
  // Each tab change is a history entry, built from the live address, as on the other tabbed pages (UX-06).
  const { view: tab, setView, liveView } = useViewParam(TABS, fallback, {}, 'tab');
  const show = (t: Tab, focus = false) => {
    setView(t);
    if (focus) requestAnimationFrame(() => document.getElementById(`control-tab-${t}`)?.focus());
  };
  useEffect(() => {
    if (hash !== '#acceptances' && hash !== '#assessors-h') return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, tab]);

  const reloadAll = () => {
    state.reload();
    asst.reload();
  };

  return (
    <Load state={state} what="the control">
      {(d) => {
        const canTalk = Boolean(asst.data?.canUse && d.viewer === 'firm');
        return (
          <>
            <NetworkBanner viewer={d.viewer} firmName={d.firmCode} />
            <p className="page-back">
              Back to <Link to={`/firm/${d.firmCode}/controls`}>all controls</Link> or the <Link to={`/firm/${d.firmCode}`}>dashboard</Link>
            </p>
            <PageHeader eyebrow={`${d.control.framework === 'CIS' ? `CIS ${d.control.controlId}` : 'Essential Eight'} in the ${areaName(d.control.domainNumber, d.control.domain)} area`} title={d.control.title} />
            <div className="tags header-tags">
              <RiskChip controlId={d.control.controlId} />
              <WaveTag wave={d.control.btiWave} />
            </div>
            <ViewTabs views={TABS} view={tab} onSelect={(t) => show(t)} liveView={liveView} label="This control" idPrefix="control" panelId={(t) => `control-panel-${t}`} className="control-tabs" />

            {/* Both panels stay mounted, so a conversation and its proposals survive a look at the other tab. */}
            <div id="control-panel-about" role="tabpanel" aria-labelledby="control-tab-about" tabIndex={0} className="lead-panel" hidden={tab !== 'about'}>
              {/* About this control: what it is, its rating, where it stands, how to put it in place, and 2025. */}
              {/* 5E (DEC-58): one column. The two answers at the top sit side by side, aligned; "Explained" is a
                  disclosure at the end of the tab (UX-04), not a third narrow column. */}
              <div className="control-about">
                <div className="control-intro">
                  <AnswerSection
                    id="plain-h"
                    level={2}
                    question="What does this control ask for?"
                    answer={<span className="lead-a-statement">{CONTROL_SUMMARIES[d.control.controlId]}</span>}
                    meaning={
                      <>
                        <strong>Why it matters:</strong> {RISK_IMPACT[d.control.controlId]?.why}
                      </>
                    }
                  >
                    <details>
                      <summary className="small">What the CIS control text says</summary>
                      <p className="small">{d.control.description}</p>
                    </details>
                  </AnswerSection>
                  <CurrentRating d={d} onAssistant={() => show('assistant', true)} />
                </div>
                <StatusCard d={d} />
                {asst.data && <Submissions list={asst.data.submissions} />}
                <ImplementationGuide controlId={d.control.controlId} firmCode={d.firmCode} />
                <Survey2025 d={d} />
                {/* UX-04 (5E pre-gate): the terms at the end, so they never push the ratings down. */}
                <ExplainedPanel show={['status', 'maturity', 'risk']} collapsed />
              </div>
            </div>

            <div id="control-panel-findings" role="tabpanel" aria-labelledby="control-tab-findings" tabIndex={0} className="lead-panel" hidden={tab !== 'findings'}>
              {d.cdw ? <Findings d={d} /> : <p className="muted">Not yet assessed.</p>}
            </div>

            <div id="control-panel-register" role="tabpanel" aria-labelledby="control-tab-register" tabIndex={0} className="lead-panel" hidden={tab !== 'register'}>
              <LinksCard d={d} />
              <AcceptancesCard d={d} onChanged={state.reload} />
            </div>

            <div id="control-panel-assistant" role="tabpanel" aria-labelledby="control-tab-assistant" tabIndex={0} className="lead-panel control-start" hidden={tab !== 'assistant'}>
              {canTalk && asst.data ? (
                <AssistantSide
                  s={asst.data}
                  firmCode={d.firmCode}
                  controlId={d.control.controlId}
                  reload={asst.reload}
                  onDraft={setDraft}
                  onAcceptanceRecorded={state.reload}
                  onPlanAdded={state.reload}
                  activeAcceptanceId={activeAcceptance(d)?.id ?? null}
                  draftReady={Boolean(shownDraft)}
                />
              ) : (
                <AssistantPlaceholder d={d} loading={!asst.data && asst.loading} failed={!asst.data && Boolean(asst.error)} onRetry={asst.reload} />
              )}
              {shownDraft && asst.data && (
                <>
                  <DraftEditor
                    key={shownDraft.id}
                    draft={shownDraft.value}
                    files={asst.data.files}
                    busy={submitting}
                    onDiscard={() => setDraft(null)}
                    onSubmit={async (body) => {
                      setSubmitting(true);
                      setSubmitError(null);
                      try {
                        await call(`/firms/${d.firmCode}/controls/${d.control.controlId}/submissions`, { method: 'POST', body: { ...body, draftMessageId: shownDraft.id } });
                        setDraft(null);
                        reloadAll();
                      } catch (e) {
                        setSubmitError(e instanceof ApiError ? e.message : 'The assessment was not submitted.');
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  />
                  {submitError && (
                    <p role="alert" className="notice">
                      {submitError}
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        );
      }}
    </Load>
  );
}

/** What the firm reported for this control in the March 2025 survey (the answer the baseline counts, and its notes). */
function Survey2025({ d }: { d: FirmControlDetail }) {
  const counted = d.survey2025.find((s) => s.round === 2) ?? d.survey2025[0];
  const notes = [...new Set(d.survey2025.map((s) => s.notes?.trim()).filter((n): n is string => Boolean(n)))];
  return (
    <Section kind="survey" question="What was reported in 2025?" id="survey-h">
      <dl className="timeline">
        <div>
          <dt>March 2025 survey</dt>
          <dd>{counted ? <Status map={SURVEY_STATUS} value={counted.status} /> : <span className="muted">Not surveyed</span>}</dd>
        </div>
      </dl>
      {notes.map((n, i) => (
        <p key={i} className="pre small">
          {n}
        </p>
      ))}
      <p className="muted small">{counted?.measure ?? 'Firm self-assessment, March 2025'}</p>
    </Section>
  );
}

/**
 * The history behind the current rating in 2026: CDW, the firm's own rating and National IT review. The March 2025
 * survey has its own section further down (DEC-49).
 */
function StatusCard({ d }: { d: FirmControlDetail }) {
  return (
    // 5E (DEC-58): the question is the heading; the three ratings under it are its answer.
    <AnswerSection id="status-h" level={2} className="status-card" question="Who rated it, and when?">
      <dl className="timeline">
        <div>
          <dt>CDW assessment, 2026</dt>
          <dd>
            {d.cdw ? (
              <>
                <Status map={CDW_GAP_STATUS} value={d.cdw.status} /> <span className="muted small">maturity {maturityLabel(d.cdw.maturity)}</span>
              </>
            ) : (
              <span className="muted">Not yet assessed</span>
            )}
          </dd>
        </div>
        <div>
          <dt>The firm's own rating</dt>
          <dd>
            {d.selfAssessment ? (
              <>
                <Status map={SELF_STATUS} value={d.selfAssessment.status} /> <span className="muted small">maturity {maturityLabel(d.selfAssessment.maturity)}</span>
              </>
            ) : (
              <span className="muted">Not rated</span>
            )}
            {d.latestSubmission && d.latestSubmission.status !== 'accepted' && d.latestSubmission.status !== 'superseded' && (
              <div>
                <Status map={SUBMISSION_STATUS} value={d.latestSubmission.status} />
              </div>
            )}
            {unsent(d) && (
              <div>
                <span className="chip tone-attention">Not sent to National IT</span>
              </div>
            )}
          </dd>
        </div>
        <div>
          <dt>National IT review</dt>
          <dd>
            {d.nationalReview ? (
              <>
                <span className="chip tone-reviewed">{resultLabel(d.nationalReview.status)}</span>{' '}
                <span className="muted small">{formatDate(d.nationalReview.reviewedAt)}</span>
              </>
            ) : (
              <span className="muted">Not reviewed yet</span>
            )}
          </dd>
        </div>
      </dl>
      {d.maturity.next && (
        <p className="small">
          <strong>To reach maturity {maturityLabel(d.maturity.next.level)}:</strong> {d.maturity.next.indicators.join('; ')}.
        </p>
      )}
      {d.selfAssessment?.note && (
        <details>
          <summary className="small">How the firm describes it today</summary>
          <p className="pre small">{d.selfAssessment.note}</p>
          <p className="muted small">
            {d.selfAssessment.updatedBy}, {formatDate(d.selfAssessment.updatedAt)}
          </p>
        </details>
      )}
    </AnswerSection>
  );
}

/**
 * The control's current rating, read-only, and where it comes from (Andres, 2026-09-25). Ratings change
 * only through the control assistant's draft, confirmed by the IT lead and sent to National for review.
 */
function CurrentRating({ d, onAssistant }: { d: FirmControlDetail; onAssistant: () => void }) {
  const c = d.current;
  const when = c?.source === 'reviewed' ? d.nationalReview?.reviewedAt : c?.source === 'self' ? d.selfAssessment?.updatedAt : null;
  const sub = d.latestSubmission;
  const tone = !c ? 'neutral' : c.source === 'reviewed' ? 'reviewed' : CDW_GAP_STATUS[c.status]?.tone ?? 'neutral';
  const pointer = d.canUpdate ? (
    <>
      To change it, talk to the{' '}
      <a href="?tab=assistant" onClick={(e) => (e.preventDefault(), onAssistant())}>
        control assistant
      </a>{' '}
      on its own tab and send its draft to National IT.
    </>
  ) : d.viewer === 'network' ? (
    "Read-only. The firm's IT leads change it through the control assistant, and National IT reviews it."
  ) : (
    `Your firm's IT leads change it through the control assistant.${d.isExecutive ? ' You approve risk acceptances.' : ''}`
  );
  // 5E (DEC-58): the question is the heading; the status is the answer, with its colour as a key mark beside it, and the
  // maturity is the chip (a state, never a repeat of the status); where it comes from is its source line.
  const level = d.maturity.current?.level;
  return (
    <AnswerSection
      id="rating-h"
      level={2}
      className={c?.source === 'self' ? 'current-rating self-box' : 'current-rating'}
      question="What is its current rating?"
      // 5E NEXT PASS 2 (E5): the status as the answer at the shared size, in its status colour (CP-06), not a smaller chip.
      answer={<span className={`rating-status tone-${tone}`}>{c ? statusText(c.status) : 'Not rated'}</span>}
      chip={{ label: `Maturity (0 to 5): ${maturityLabel(level)}`, tone: 'neutral' }}
      meaning={<span className="rating-pointer">{pointer}</span>}
      source={c ? `${SOURCE[c.source]}${when ? `, ${formatDate(when)}` : ''}` : undefined}
    >
      {sub?.status === 'submitted' && (
        <p className="small">
          <Status map={SUBMISSION_STATUS} value={sub.status} /> Sent {formatDate(sub.submittedAt)}: {statusText(sub.proposedStatus)}
          {sub.proposedMaturity !== null && `, maturity ${maturityLabel(sub.proposedMaturity)}`}
        </p>
      )}
      {sub?.status === 'returned' && (
        <div className="notice small">
          <strong>Returned by National IT with questions:</strong> {sub.reviewNote}
        </div>
      )}
    </AnswerSection>
  );
}

/** Stands in for the assistant while it loads, if it cannot load, or for people who read but do not rate. */
function AssistantPlaceholder({ d, loading, failed, onRetry }: { d: FirmControlDetail; loading: boolean; failed: boolean; onRetry: () => void }) {
  return (
    <section className="assistant-side" aria-labelledby="assistant-h">
      <h2 id="assistant-h">Control assistant</h2>
      {loading ? (
        <p className="assistant-note" role="status">
          Opening the control assistant…
        </p>
      ) : failed ? (
        <>
          <p className="assistant-error">The control assistant did not load.</p>
          <button className="pill secondary-dark" onClick={onRetry}>
            Try again
          </button>
        </>
      ) : d.viewer === 'network' ? (
        <p className="assistant-note">
          You are viewing this firm read-only. The firm's IT leads use the assistant here; what they send to National IT shows on this page. Their conversations stay private to the firm.
        </p>
      ) : (
        <p className="assistant-note">
          The control assistant is for your firm's IT leads. They use it to check evidence and send assessments to National IT, which show on this page.
        </p>
      )}
    </section>
  );
}

function Findings({ d }: { d: FirmControlDetail }) {
  const c = d.cdw!;
  return (
    <Section kind="assessors" question="What did the assessors find?" id="assessors-h">
      {c.observedState && (
        <>
          <h3>Observed</h3>
          <p className="pre">{c.observedState}</p>
        </>
      )}
      {c.gap && (
        <>
          <h3>Gap</h3>
          <p className="pre">{c.gap}</p>
        </>
      )}
      {c.recommendation && (
        <>
          <h3>Recommendation</h3>
          <p className="pre">{c.recommendation}</p>
        </>
      )}
      {c.pentestRefs && (
        <>
          <h3>Related penetration test findings</h3>
          <p className="pre">{c.pentestRefs}</p>
        </>
      )}
      <p className="muted small">{c.measure}</p>
    </Section>
  );
}

function LinksCard({ d }: { d: FirmControlDetail }) {
  if (!d.risks.length && !d.actionPlans.length) return null;
  return (
    <Section kind="plans" question="What risks and plans relate to it?" id="links-h">
      {d.risks.length > 0 && (
        <ul className="plain risk-list">
          {d.risks.map((r) => (
            <li key={r.id}>
              <SeverityChip severity={r.severity} />
              <Link to={`/firm/${d.firmCode}/risks`}>
                <span className="cid">{r.code}</span> {r.title}
              </Link>
              {r.linkedByFamily && <span className="chip tone-neutral link-review">{FAMILY_LINK_LABEL}</span>}
            </li>
          ))}
        </ul>
      )}
      {d.actionPlans.length > 0 && (
        <ul className="plain">
          {d.actionPlans.map((p) => (
            <li key={p.id}>
              <strong>{p.code}</strong> {p.objective} <Status map={DELIVERY_STATUS} value={p.deliveryStatus} />{' '}
              <span className="muted small">
                {p.progress ?? 0}% done{p.targetDate && `, target ${formatDate(p.targetDate)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** The control's accepted decision, which a new one replaces (supersedes); the one rule for both ways of recording (CP-05). */
const activeAcceptance = (d: FirmControlDetail) => d.riskAcceptances.find((a) => a.status === 'active' && !a.riskId);

function AcceptancesCard({ d, onChanged }: { d: FirmControlDetail; onChanged: () => void }) {
  const call = useApiClient();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ rationale: '', compensatingControls: '', approverName: '', approverRole: '', decidedOn: today, reviewDueOn: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const active = activeAcceptance(d);
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setMessage(null);
    try {
      await fn();
      setMessage({ ok: true, text: ok });
      onChanged();
      return true;
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Something went wrong.' });
      return false;
    }
  };
  // Firms without a risk assessment explain why a control is not in place yet (Andres, 2026-09-23).
  const title = d.assessed ? 'Risk acceptance' : 'Explanation: why this is not in place yet';
  const intro = d.assessed
    ? "A risk acceptance is your firm's business decision to carry a risk for a set period. Your IT group records it; a member of your executive group approves it."
    : 'Your firm has not had its risk assessment yet. If this control has not been addressed since March 2025, explain why, what reduces the risk meanwhile, and who decided. Your executive group approves it.';
  const ask = `Please review this ${d.assessed ? 'risk acceptance' : 'explanation of why the control is not in place yet'} as a devil's advocate, weighing the risk against the impact on our operations.\n\nRationale: ${f.rationale || '(not written yet)'}\nCompensating controls: ${f.compensatingControls || '(none yet)'}`;
  return (
    <Section kind="decision" question={d.assessed ? 'Has the firm accepted a risk here?' : 'Has the firm explained why it is not in place yet?'} id="acceptance-h" anchor="acceptances">
      <p className="muted small">{intro}</p>
      {d.riskAcceptances.map((a: RiskAcceptance) => (
        <div key={a.id} className="decision">
          <p>
            <Status map={ACCEPTANCE_STATUS} value={a.status} /> {acceptanceLine(a)}
          </p>
          <p className="pre">{a.rationale}</p>
          {a.compensatingControls && (
            <p className="pre">
              <strong>Compensating controls:</strong> {a.compensatingControls}
            </p>
          )}
          <div className="actions">
            {a.status === 'pending_approval' && d.isExecutive && !a.createdByMe && (
              <button className="pill" onClick={() => act(() => call(`/firms/${d.firmCode}/risk-acceptances/${a.id}/approve`, { method: 'POST' }), 'Risk acceptance approved.')}>
                Approve
              </button>
            )}
            {a.status === 'pending_approval' && d.isExecutive && a.createdByMe && <span className="muted small">You recorded this, so someone else approves it.</span>}
            {(a.status === 'pending_approval' || a.status === 'active') && d.canUpdate && (
              <button className="pill secondary-dark" onClick={() => act(() => call(`/firms/${d.firmCode}/risk-acceptances/${a.id}/withdraw`, { method: 'POST' }), 'Withdrawn.')}>
                Withdraw
              </button>
            )}
          </div>
        </div>
      ))}
      {message && (
        <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'ok' : 'notice'}>
          {message.text}
        </p>
      )}
      {d.canUpdate &&
        (open ? (
          <form
            className="form"
            aria-label={title}
            onSubmit={async (e) => {
              e.preventDefault();
              if (await act(() => call(`/firms/${d.firmCode}/controls/${d.control.controlId}/risk-acceptances`, { method: 'POST', body: { ...f, supersedes: active?.id ?? null } }), 'Recorded. It now awaits approval.'))
                setOpen(false);
            }}
          >
            <label>
              {d.assessed ? 'Why the firm accepts this risk' : 'Why it is not in place yet'}
              <textarea required minLength={20} maxLength={4000} rows={5} value={f.rationale} onChange={set('rationale')} />
            </label>
            <label>
              Compensating controls
              <textarea maxLength={4000} rows={3} value={f.compensatingControls} onChange={set('compensatingControls')} />
            </label>
            <div className="row">
              <label>
                Decided by
                <input required maxLength={200} value={f.approverName} onChange={set('approverName')} />
              </label>
              <label>
                Their role
                <input required maxLength={120} value={f.approverRole} onChange={set('approverRole')} placeholder="for example, Managing Partner" />
              </label>
            </div>
            <div className="row">
              <label>
                Decision date
                <input type="date" required max={today} value={f.decidedOn} onChange={set('decidedOn')} />
              </label>
              <label>
                Review by (within a year)
                <input type="date" required value={f.reviewDueOn} onChange={set('reviewDueOn')} />
              </label>
            </div>
            <div className="actions">
              <button className="pill">Record for approval</button>
              <Link className="pill assistant-pill" to={`?ask=${encodeURIComponent(ask)}`} replace>
                Have the assistant challenge it
              </Link>
              <button type="button" className="pill secondary-dark" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button className="pill secondary-dark" onClick={() => setOpen(true)}>
            {d.assessed ? (active ? 'Record a replacement decision' : 'Record a risk acceptance') : 'Write an explanation'}
          </button>
        ))}
    </Section>
  );
}
